import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Cpu, PencilSimple, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";

const DEFAULT_ISSUE_TYPES = [
  "login_error", "crash", "slow_performance", "connectivity", "access_denied",
  "hardware_failure", "configuration", "feature_request", "data_loss", "other",
];
const DEFAULT_SYSTEMS = [
  "Salesforce", "Outlook", "Gmail", "VPN", "Wi-Fi", "Windows", "macOS",
  "Slack", "Zoom", "Teams", "Excel", "Word", "Chrome", "Active Directory",
  "Printer", "SharePoint", "OneDrive", "Jira", "Confluence", "GitHub",
];

const ADD_NEW = "__ADD_NEW__";

export function ClassificationCard({ ticket, onChange, empty, systemsList, issueTypesList, onTaxonomyUpdated }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [editing, setEditing] = useState(false);
  const [system, setSystem] = useState(ticket.system || "");
  const [issueType, setIssueType] = useState(ticket.issue_type || "");
  const [saving, setSaving] = useState(false);
  const [addingSystem, setAddingSystem] = useState(false);
  const [newSystemName, setNewSystemName] = useState("");
  const [savingSystem, setSavingSystem] = useState(false);
  const SYSTEMS = systemsList && systemsList.length ? systemsList : DEFAULT_SYSTEMS;
  const ISSUE_TYPES = issueTypesList && issueTypesList.length ? issueTypesList : DEFAULT_ISSUE_TYPES;

  useEffect(() => {
    setSystem(ticket.system || "");
    setIssueType(ticket.issue_type || "");
  }, [ticket.system, ticket.issue_type]);

  const save = async () => {
    setSaving(true);
    try {
      await onChange({ system: system.trim() || null, issue_type: issueType || null });
      setEditing(false);
    } finally { setSaving(false); }
  };

  const cancel = () => {
    setSystem(ticket.system || "");
    setIssueType(ticket.issue_type || "");
    setEditing(false);
    setAddingSystem(false);
    setNewSystemName("");
  };

  const handleSystemChange = (val) => {
    if (val === ADD_NEW) {
      setAddingSystem(true);
      setNewSystemName("");
      return;
    }
    setSystem(val === "none" ? "" : val);
  };

  const submitNewSystem = async () => {
    const name = newSystemName.trim();
    if (!name) return;
    setSavingSystem(true);
    try {
      await api.post("/settings/taxonomy/add", { list_key: "systems", value: name });
      setSystem(name);
      setAddingSystem(false);
      setNewSystemName("");
      toast.success(`Added "${name}" to systems`);
      onTaxonomyUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add system");
    } finally {
      setSavingSystem(false);
    }
  };

  // §11 — accept an AI-suggested system (one not yet in the taxonomy).
  // Adds it to `systems` taxonomy AND applies it to the ticket in one
  // click, clearing `system_suggested`. Admin-only.
  const suggestedSystem = ticket.system_suggested;
  const [applyingSuggested, setApplyingSuggested] = useState(false);
  const applySuggestedSystem = async () => {
    if (!suggestedSystem) return;
    setApplyingSuggested(true);
    try {
      await api.post("/settings/taxonomy/add", { list_key: "systems", value: suggestedSystem });
      await onChange({ system: suggestedSystem, system_suggested: null });
      toast.success(`Added "${suggestedSystem}" and applied it`);
      onTaxonomyUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to apply suggestion");
    } finally {
      setApplyingSuggested(false);
    }
  };
  const dismissSuggestedSystem = async () => {
    await onChange({ system_suggested: null });
  };

  return (
    <div className="hairline rounded-lg p-3 bg-white space-y-2" data-testid="ticket-classification">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase text-slate-500 tracking-wider font-medium flex items-center gap-1">
          <Cpu size={11} weight="duotone" /> Classification
        </div>
        {!editing && (
          <button
            data-testid="classification-edit-button"
            onClick={() => setEditing(true)}
            className="text-[11px] text-slate-500 hover:text-slate-900 flex items-center gap-1"
          >
            <PencilSimple size={11} /> Edit
          </button>
        )}
      </div>

      {!editing ? (
        <div className="flex flex-wrap gap-1.5">
          {ticket.system && <span className="chip !text-[#8B87C8] !border-[#4f4c90]/30 bg-[#4f4c90]/10">System · {ticket.system}</span>}
          {ticket.issue_type && <span className="chip !text-[#FB923C] !border-[#FB923C]/30 bg-[#FB923C]/10">{ticket.issue_type.replace(/_/g, " ")}</span>}
          {empty && !suggestedSystem && <div className="text-[11px] text-slate-500">Not classified yet — click Edit to add.</div>}
          {suggestedSystem && (
            <div
              data-testid="classification-suggested-system"
              className="w-full mt-1 flex items-center justify-between gap-2 rounded-md border border-amber-300 bg-amber-50 px-2 py-1.5"
            >
              <span className="text-[11px] text-amber-900">
                AI suggested system: <strong>{suggestedSystem}</strong> (not in taxonomy)
              </span>
              {isAdmin && (
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={applySuggestedSystem}
                    disabled={applyingSuggested}
                    data-testid="classification-apply-suggested-system"
                    className="text-[11px] text-emerald-700 hover:text-emerald-900 font-medium"
                  >
                    {applyingSuggested ? "Adding…" : `Add "${suggestedSystem}"`}
                  </button>
                  <span className="text-amber-300">·</span>
                  <button
                    type="button"
                    onClick={dismissSuggestedSystem}
                    data-testid="classification-dismiss-suggested-system"
                    className="text-[11px] text-slate-500 hover:text-slate-800"
                  >
                    Dismiss
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-2 pt-1">
          <div>
            <Label className="text-[10px] uppercase text-slate-500 tracking-wider">System</Label>
            {addingSystem ? (
              <div className="mt-1 flex items-center gap-1.5">
                <Input
                  autoFocus
                  value={newSystemName}
                  onChange={(e) => setNewSystemName(e.target.value)}
                  placeholder="e.g., Concur"
                  className="h-8 bg-white border-slate-200 text-sm"
                  data-testid="classification-new-system-input"
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitNewSystem(); } }}
                />
                <Button
                  type="button"
                  onClick={submitNewSystem}
                  disabled={savingSystem || !newSystemName.trim()}
                  className="h-8 text-[11px] bg-[#4f4c90] hover:bg-[#5F5CA5] text-white px-3"
                  data-testid="classification-new-system-save"
                >
                  {savingSystem ? "Adding…" : "Add"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => { setAddingSystem(false); setNewSystemName(""); }}
                  className="h-8 text-[11px] text-slate-500 hover:text-slate-800 px-2"
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <Select value={system || "none"} onValueChange={handleSystemChange}>
                <SelectTrigger data-testid="classification-system-select" className="mt-1 bg-[#FFFFFF] border-slate-200 h-8">
                  <SelectValue placeholder="Choose…" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <SelectItem value="none">— none —</SelectItem>
                  {SYSTEMS.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                  {isAdmin && (
                    <SelectItem value={ADD_NEW} className="text-[#4f4c90] font-medium">
                      <span className="inline-flex items-center gap-1"><Plus size={11} /> Add new system…</span>
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            )}
          </div>
          <div>
            <Label className="text-[10px] uppercase text-slate-500 tracking-wider">Issue type</Label>
            <Select value={issueType || "none"} onValueChange={(v) => setIssueType(v === "none" ? "" : v)}>
              <SelectTrigger data-testid="classification-issue-select" className="mt-1 bg-[#FFFFFF] border-slate-200 h-8">
                <SelectValue placeholder="Choose…" />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200">
                <SelectItem value="none">— none —</SelectItem>
                {ISSUE_TYPES.map((it) => (
                  <SelectItem key={it} value={it} className="capitalize">{it.replace(/_/g, " ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2 pt-1">
            <Button
              data-testid="classification-save-button"
              onClick={save}
              disabled={saving}
              className="bg-[#4f4c90] hover:bg-[#5F5CA5] h-7 text-xs"
            >
              {saving ? "Saving…" : "Save"}
            </Button>
            <Button
              onClick={cancel}
              data-testid="classification-cancel-button"
              variant="ghost"
              className="h-7 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-100"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
